import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { validateEmail, validatePhone, validateRequired, validateMinLength } from '../../utils/validation';
import emailjs from '@emailjs/browser';

// Initialisation d'EmailJS
emailjs.init({
  publicKey: 'HLhslkO794iN8ousF',
});

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  projectType: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
}

// Configuration EmailJS
const SERVICE_ID = 'service_bff8c1n'; // ID de service EmailJS
const TEMPLATE_ID = 'template_bt3yscv'; // ID de template pour l'email principal
// Note: La réponse automatique est maintenant gérée directement par EmailJS via la fonction Auto-Reply

const ContactForm: React.FC = () => {
  const location = useLocation();
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    projectType: 'construction'
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const projectTypes = [
    { value: 'construction', label: 'Travaux neufs' },
    { value: 'renovation', label: 'Rénovation' },
    { value: 'maintenance', label: 'Réparation et maintenance' },
    { value: 'autre', label: 'Autre' }
  ];

  // Récupérer les paramètres d'URL et pré-remplir le formulaire
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const serviceTitle = params.get('service');
      const serviceType = params.get('type');
      const projectTitle = params.get('project');
      
      // Si un service est spécifié dans l'URL
      if (serviceTitle) {
        // Déterminer le type de projet en fonction de l'ID du service
        let projectType = 'autre';
        if (serviceType === '1') projectType = 'construction';
        if (serviceType === '2') projectType = 'renovation';
        if (serviceType === '3') projectType = 'maintenance';
        
        // Construire un message par défaut
        const defaultMessage = `Bonjour,\n\nJe souhaite obtenir un devis pour votre service "${serviceTitle}".\n\nMerci de me contacter pour discuter de mon projet.`;
        
        // Mettre à jour le formulaire
        setFormData(prev => ({
          ...prev,
          subject: `Demande de devis - ${serviceTitle}`,
          message: defaultMessage,
          projectType
        }));
        
        console.log("Formulaire pré-rempli avec le service:", serviceTitle);
      }
      // Si un projet est spécifié dans l'URL
      else if (projectTitle) {
        // Déterminer le type de projet en fonction de la catégorie du projet
        let projectType = 'autre';
        if (serviceType === 'construction' || serviceType === 'Collège Descartes') projectType = 'construction';
        if (serviceType === 'renovation' || serviceType === 'Ecole Elementaire Des Sapins') projectType = 'renovation';
        if (serviceType === 'maintenance') projectType = 'maintenance';
        
        // Construire un message par défaut
        const defaultMessage = `Bonjour,\n\nJe souhaite obtenir un devis pour un projet similaire à "${projectTitle}".\n\nMerci de me contacter pour discuter de mon projet.`;
        
        // Mettre à jour le formulaire
        setFormData(prev => ({
          ...prev,
          subject: `Demande de devis - Projet similaire à ${projectTitle}`,
          message: defaultMessage,
          projectType
        }));
        
        console.log("Formulaire pré-rempli avec le projet:", projectTitle);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des paramètres d'URL:", error);
    }
  }, [location.search]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!validateRequired(formData.name)) {
      newErrors.name = 'Veuillez entrer votre nom';
    }
    
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Veuillez entrer votre email';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Veuillez entrer un email valide';
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Veuillez entrer un numéro de téléphone valide';
    }
    
    if (!validateRequired(formData.subject)) {
      newErrors.subject = 'Veuillez entrer un sujet';
    }
    
    if (!validateRequired(formData.message)) {
      newErrors.message = 'Veuillez entrer votre message';
    } else if (!validateMinLength(formData.message, 20)) {
      newErrors.message = 'Votre message doit contenir au moins 20 caractères';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Préparation des paramètres pour EmailJS
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        projectType: projectTypes.find(type => type.value === formData.projectType)?.label || formData.projectType
      };
      
      // Préparation des paramètres pour l'email principal avec indication d'envoyer une réponse automatique
      // Création d'un nouvel objet qui inclut tous les paramètres existants plus les variables nécessaires pour la réponse auto
      const emailParams = {
        ...templateParams,
        reply_to: formData.email, // Ajoute l'email du client comme reply_to
        type: projectTypes.find(type => type.value === formData.projectType)?.label || formData.projectType, // Pour {{type}} dans le template
        name: formData.name // Pour {{name}} dans le template
      };
      
      // Envoi de l'email principal via EmailJS
      const result = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        emailParams
      );
      
      // Si l'email principal a été envoyé avec succès, nous n'envoyons pas de réponse automatique séparée
      // car elle est gérée directement par EmailJS
      if (result.status === 200) {
        console.log('Email envoyé avec succès. La réponse automatique sera gérée par EmailJS.');
        
        // Note: Nous ne faisons plus d'appel séparé pour la réponse automatique
        // EmailJS gère cela automatiquement avec la fonction "Auto-Reply" dans leur interface
        
        // Success response
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          projectType: 'construction'
        });
      } else {
        // Error response
        console.error('Erreur lors de l\'envoi de l\'email:', result.text);
        setSubmitStatus('error');
      }
    } catch (error) {
      // Error response
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 max-w-md mx-auto w-full">
      {submitStatus === 'success' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-green-100 text-green-800 rounded-md flex items-center"
        >
          <FontAwesomeIcon icon={faCheck} className="mr-3 text-green-500" />
          <span>Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.</span>
        </motion.div>
      )}
      
      {submitStatus === 'error' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-100 text-red-800 rounded-md flex items-center"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3 text-red-500" />
          <span>Une erreur est survenue lors de l'envoi de votre message. Veuillez réessayer ultérieurement.</span>
        </motion.div>
      )}
      
      <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col space-y-4">
        {/* Nom */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Nom complet <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Votre nom"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-500">{errors.name}</p>
          )}
        </div>
        
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Votre email"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email}</p>
          )}
        </div>
        
        {/* Téléphone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Téléphone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Votre numéro de téléphone"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
          )}
        </div>
        
        {/* Type de projet */}
        <div>
          <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-1">
            Type de projet
          </label>
          <select
            id="projectType"
            name="projectType"
            value={formData.projectType}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {projectTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        
        {/* Sujet */}
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
            Sujet <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.subject ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Sujet de votre message"
          />
          {errors.subject && (
            <p className="mt-1 text-sm text-red-500">{errors.subject}</p>
          )}
        </div>
        
        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={6}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.message ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Votre message"
          ></textarea>
          {errors.message && (
            <p className="mt-1 text-sm text-red-500">{errors.message}</p>
          )}
        </div>
        
        <div className="mt-6">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-6 bg-primary hover:bg-primary-dark text-white font-medium rounded-md transition-colors duration-300 ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
